// src/components/lekhi/AudioView.js
import React, { useState, useRef, useEffect } from 'react';
import './AudioView.css';
import { LekhiWatermark, lekhiProtectProps, LEKHI_PROTECT_CLASS } from './LekhiProtect';

const SUPPORT_WHATSAPP = '919359246193';

// Same WhatsApp icon as the login screen
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="white" width="24" height="24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// locked = true  → daily training: no skipping ahead, Continue only after full play
// locked = false → Library: free to scrub, Continue always visible (default)
const AudioView = ({ audio, blockLabel, topicName, language, onComplete, onBack, locked = false }) => {
  const audioRef = useRef(null);
  const maxPlayedRef = useRef(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [hasFinished, setHasFinished] = useState(false);
  const [loadError, setLoadError] = useState(!audio?.audioURL);

  const labels = language === 'mr'
    ? {
        back: '← मागे',
        next: 'पुढे',
        hint: "ऑडिओ पूर्ण ऐका, मग 'पुढे' बटण दिसेल",
        errorTitle: 'ऑडिओ चालू होत नाही',
        errorSub: 'इंटरनेट तपासा आणि हे पान पुन्हा उघडा. तरीही चालू न झाल्यास आम्हाला कळवा.',
        contact: 'सहाय्यता संपर्क'
      }
    : {
        back: '← Back',
        next: 'Continue',
        hint: 'Listen to the full audio to continue',
        errorTitle: 'Audio is not playing',
        errorSub: 'Check your internet and open this page again. If it still does not play, tell us.',
        contact: 'Contact Support'
      };

  useEffect(() => {
    const audioEl = audioRef.current;
    if (!audioEl) return;

    const handleLoadedMetadata = () => setDuration(audioEl.duration);
    const handleTimeUpdate = () => {
      // Remember the furthest point genuinely listened to
      if (!audioEl.seeking && audioEl.currentTime > maxPlayedRef.current) {
        maxPlayedRef.current = audioEl.currentTime;
      }
      setCurrentTime(audioEl.currentTime);
    };
    // Locked: any jump forward (e.g. from phone notification controls) snaps back
    const handleSeeking = () => {
      if (locked && audioEl.currentTime > maxPlayedRef.current + 1) {
        audioEl.currentTime = maxPlayedRef.current;
      }
    };
    const handleEnded = () => {
      setIsPlaying(false);
      setHasFinished(true);
    };
    const handleError = () => {
      setIsPlaying(false);
      setLoadError(true);
    };

    audioEl.addEventListener('loadedmetadata', handleLoadedMetadata);
    audioEl.addEventListener('timeupdate', handleTimeUpdate);
    audioEl.addEventListener('seeking', handleSeeking);
    audioEl.addEventListener('ended', handleEnded);
    audioEl.addEventListener('error', handleError);

    return () => {
      audioEl.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audioEl.removeEventListener('timeupdate', handleTimeUpdate);
      audioEl.removeEventListener('seeking', handleSeeking);
      audioEl.removeEventListener('ended', handleEnded);
      audioEl.removeEventListener('error', handleError);
      audioEl.pause();
    };
  }, [locked]);

  const togglePlayPause = () => {
    const audioEl = audioRef.current;
    if (!audioEl) return;
    if (isPlaying) {
      audioEl.pause();
      setIsPlaying(false);
    } else {
      audioEl.play().catch(() => {});
      setIsPlaying(true);
    }
  };

  const handleReplay = () => {
    const audioEl = audioRef.current;
    if (!audioEl) return;
    audioEl.currentTime = 0;
    audioEl.play().catch(() => {});
    setIsPlaying(true);
  };

  const handleSeek = (e) => {
    const audioEl = audioRef.current;
    if (!audioEl || !duration) return;
    const newTime = (e.target.value / 100) * duration;
    audioEl.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e) => {
    const newVolume = e.target.value / 100;
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const handleWhatsApp = () => {
    const where = [blockLabel, topicName].filter(Boolean).join(' - ');
    const message = language === 'mr'
      ? `नमस्कार, माझा लेखी ऑडिओ चालू होत नाही. ${where}`
      : `Hi, my Lekhi audio is not playing. ${where}`;
    window.open(`https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const canContinue = !locked || hasFinished;

  return (
    <div
      className={`lk-audio-container ${LEKHI_PROTECT_CLASS}`}
      {...lekhiProtectProps}
    >
      <LekhiWatermark />

      <div className="lk-audio-header">
        <button className="back-btn" onClick={onBack}>{labels.back}</button>
        <div className="lk-audio-title">{blockLabel}</div>
        <div style={{ width: 60 }} />
      </div>

      <div className="lk-audio-body">
        <div className="lk-audio-card">
          <div className="lk-audio-label">{blockLabel}</div>
          {topicName && <div className="lk-audio-topic">{topicName}</div>}

          <audio ref={audioRef} src={audio?.audioURL} preload="metadata" />

          {loadError ? (
            <div className="lk-audio-error">
              <div className="lk-audio-error-title">{labels.errorTitle}</div>
              <div className="lk-audio-error-sub">{labels.errorSub}</div>
              <button className="lk-audio-whatsapp-btn" onClick={handleWhatsApp}
                title={labels.contact} aria-label={labels.contact}>
                <WhatsAppIcon />
              </button>
            </div>
          ) : (
            <>
              <button
                className={`lk-audio-play-btn ${isPlaying ? 'playing' : ''}`}
                onClick={togglePlayPause}
              >
                {isPlaying ? '⏸' : '▶'}
              </button>

              <div className="lk-audio-progress-row">
                <span className="lk-audio-time">{formatTime(currentTime)}</span>
                {locked ? (
                  <div className="lk-audio-progress-static">
                    <div className="lk-audio-progress-fill" style={{ width: `${progressPercent}%` }} />
                  </div>
                ) : (
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={progressPercent}
                    onChange={handleSeek}
                    className="lk-audio-progress-bar"
                    style={{ '--progress': `${progressPercent}%` }}
                  />
                )}
                <span className="lk-audio-time">{formatTime(duration)}</span>
              </div>

              <div className="lk-audio-controls">
                <button className="lk-audio-control-btn" onClick={handleReplay} title="Replay">
                  ↻
                </button>
                <div className="lk-audio-volume-wrap">
                  <button
                    className="lk-audio-control-btn"
                    onClick={() => setShowVolumeSlider(!showVolumeSlider)}
                    title="Volume"
                  >
                    {volume === 0 ? '🔇' : volume < 0.5 ? '🔈' : '🔊'}
                  </button>
                  {showVolumeSlider && (
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={volume * 100}
                      onChange={handleVolumeChange}
                      className="lk-audio-volume-slider"
                    />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="lk-audio-footer lk-above-watermark">
        {canContinue ? (
          <button className="lk-audio-next-btn" onClick={onComplete}>{labels.next}</button>
        ) : !loadError ? (
          <div className="lk-audio-hint">{labels.hint}</div>
        ) : null}
      </div>
    </div>
  );
};

export default AudioView;